<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions :data-ready="dataReadyTables" :filename-prefix="title" :multiple="true"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />

    <div v-for="(tables, index) in tablesData" :key="index">
      <table-renderer :data-table="tables.dataTable1.dataTable" :dimension-fields="dimensionColumnsTable1"
        :measure-fields="getMeasureColumnsTable1(tables)" :table-header="tables.dataTable1.tableHeader"
        :group-headers="groupHeadersTable1" :data-ready="dataReadyTables1" :custom-table-class="customTableClass"
        ref="tableRenderers1" />

      <table-renderer :data-table="tables.dataTable2.dataTable" :dimension-fields="dimensionColumnsTable2"
        :measure-fields="measureColumnsTable2" :table-header="tables.dataTable2.tableHeader"
        :group-headers="groupHeadersTable2" :totals-data="tables.dataTable2.totalsData" :data-ready="dataReadyTables2"
        :custom-table-class="customTableClass" ref="tableRenderers2" />

      <table-renderer :data-table="tables.dataTable3.dataTable" :measure-fields="getMeasureColumnsTable3(tables)"
        :table-header="tables.dataTable3.tableHeader" :group-headers="groupHeadersTable3" :data-ready="dataReadyTables3"
        :custom-table-class="customTableClass" ref="tableRenderers3" />

      <hr v-if="index < tablesData.length - 1" class="separator">
    </div>
  </b-container>
</template>

<script>
import TableTools from "@/utils/tableTools";
import TableRenderer from "@/components/TableRenderer.vue";
import dimensionEnum from "@/model/dimensionEnum";
import paths from "@/router/pathConstants";
import commonActions from "@/components/pages/commonActions.vue";

import { OperationsPeriodical } from "@/router/routeNames";
import { mapState } from "vuex";

export default {
  name: OperationsPeriodical,
  components: {
    TableRenderer,
    commonActions
  },
  data() {
    return {
      /** Filtre venant du FilterPicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "Site.label",
       *      operator: "equals",
       *      values: ["Site 1"]
       *    }
       */
      groupKey: dimensionEnum.SITE_SITE_CATEGORY,
      groupLabel: dimensionEnum.SITE_SITE_CATEGORY_NAME,
      dimensionColumnsTable1: [],
      //defaultSortedColumnTable1: "",
      //totalsDataTable1: null,
      //defaultSortedColumnTable2: "",
    };
  },
  computed: {
    ...mapState({
      tablesData: (state) => state.operations.tablesData,
      dataReadyTables1: (state) => state.operations.dataReadyTables1,
      dataReadyTables2: (state) => state.operations.dataReadyTables2,
      dataReadyTables3: (state) => state.operations.dataReadyTables3,
      measureColumnsTable1: (state) => state.operations.measureColumnsTable1,
      measureColumnsTable2: (state) => state.operations.measureColumnsTable2,
      measureColumnsTable3: (state) => state.operations.measureColumnsTable3,
      dimensionColumnsTable2: (state) => state.operations.dimensionColmunTable2,
      groupHeadersTable1: (state) => state.operations.groupHeadersTable1,
      groupHeadersTable2: (state) => state.operations.groupHeadersTable2,
      groupHeadersTable3: (state) => state.operations.groupHeadersTable3,
    }),
    dataReadyTables() {
      return this.dataReadyTables1 && this.dataReadyTables2 && this.dataReadyTables3;
    },
    title() {
      return this.$t("title.operations_periodical");
    },
    customTableClass() {
      return "table-width-medium";
    },
    /** Etat des données qui font appel à la base */
    queriedDataReady() {
      return this.dataReadyTables1 && this.dataReadyTables2;
    }
  },
  watch: {
    queriedDataReady(newValue, oldValue) {
      if (newValue && !oldValue) this.$store.dispatch('operations/updateTables3');
    },
  },
  mounted() {
    this.$store.commit('operations/setIsOmniplay', false);
    this.$store.dispatch('operations/initDisplayTable1');
    this.$store.dispatch('operations/initDisplayTable2');
    this.$store.dispatch('operations/initDisplayTable3');
  },
  created() {
    // Si les données étaient déjà prêtes, on initialise la table, sinon le watcher s'en occupe
    if (this.queriedDataReady) this.$store.dispatch('operations/updateTables3');
  },
  methods: {
    commonFiltersUpdated(value) {
      this.$store.commit("operations/setFilters", value.filters);
      this.$store.commit("operations/setTimeDimension", value.timeDimension || null);
      this.$store.commit("operations/setSiteValues", value.siteValues);
      this.$store.dispatch("operations/initTablesData");
    },
    getMeasureColumnsTable1(table) {
      return table.dataTable1.dynamicMeasureColumnsTable ?? this.measureColumnsTable1;
    },
    getMeasureColumnsTable3(table) {
      return table.dataTable3.dynamicMeasureColumnsTable ?? this.measureColumnsTable3;
    },
    downloadPdf(data) {
      if (
        this.dataReadyTables1 &&
        this.dataReadyTables2 &&
        this.dataReadyTables3
      ) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (
        this.dataReadyTables1 &&
        this.dataReadyTables2 &&
        this.dataReadyTables3
      ) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (
        this.dataReadyTables1 &&
        this.dataReadyTables2 &&
        this.dataReadyTables3
      ) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      let datasForExport = [];

      this.tablesData.forEach((table, index) => {
        const renderer1 = this.$refs.tableRenderers1?.[index];
        const renderer2 = this.$refs.tableRenderers2?.[index];
        const renderer3 = this.$refs.tableRenderers3?.[index];

        const sortKey1 = renderer1?.sortedColumn || null;
        const sortKey2 = renderer2?.sortedColumn || null;
        const sortKey3 = renderer3?.sortedColumn || null;

        const data1 = TableTools.Helper.assembleExportDataTable(
          table.dataTable1.dataTable,
          this.dimensionColumnsTable1,
          this.measureColumnsTable1,
          this.groupHeadersTable1,
          table.dataTable1.tableHeader,
          sortKey1,
          null,
          null,
          table.dataTable1.tableNumber
        );
        datasForExport.push(data1);
        const data2 = TableTools.Helper.assembleExportDataTable(
          table.dataTable2.dataTable,
          this.dimensionColumnsTable2,
          this.measureColumnsTable2,
          this.groupHeadersTable2,
          table.dataTable2.tableHeader,
          sortKey2,
          null,
          table.dataTable2.totalsData,
          table.dataTable2.tableNumber
        );
        datasForExport.push(data2);
        const data3 = TableTools.Helper.assembleExportDataTable(
          table.dataTable3.dataTable,
          null,
          this.measureColumnsTable3,
          this.groupHeadersTable3,
          table.dataTable3.tableHeader,
          sortKey3,
          null,
          null,
          table.dataTable3.tableNumber
        );
        datasForExport.push(data3);
      });

      TableTools.Helper.storeExportData(
        datasForExport,
        this.title,
        subtitles,
        filename
      );
    },
  },
};
</script>
